import {
  applyCollectionQueryState,
  readCollectionQueryState,
} from "../filters/collectionQueryState";
import {
  readStickyFilterState,
  writeStickyFilterState,
} from "../filters/stickyFilters";

/**
 * Extracted verbatim from List.vue (asana-995, Task 6) -- the sticky
 * lifecycle is now shared so a page with no router of its own (my-cases.vue)
 * gets it too. A host still owns the URL-writing half (List.vue keeps its
 * own `$route.query` watcher, and overrides `loadQueryState` with a
 * router-aware version); this mixin owns persistence and rehydration for
 * every host, including the `filterQueryValues` watcher -- List.vue's own
 * `filterQueryValues` watcher (URL-pushing only) no longer calls
 * persistStickyState() itself, to avoid double-triggering it.
 *
 * One deliberate change from the original: this watches
 * `layout.collection.optionQueryValues`, NOT `remoteQueryOptions` --
 * `remoteQueryOptions.sortBy` holds live CrudProperty instances, which is
 * the exact shape that hung /my-cases with a `deep: true` watch (Task 3,
 * plugins/Cruxtify/tests/cycle-guard.spec.ts). This watcher was inert on My
 * Cases before Task 6 (List.vue was never used there); it is not inert once
 * this mixin lands there.
 *
 * `mounted()` is deliberately NOT a lifecycle hook here. Vue merges a
 * mixin's own `mounted()` before the host component's, and List.vue's
 * sticky rehydrate must run strictly after its own `resetQuery(this)` --
 * relying on Vue's merge order for that would be fragile. Call
 * `initStickyFilters()` explicitly, after any host-specific mount-time setup
 * (List.vue does; my-cases.vue has none, so it calls it first).
 */
export default {
  data() {
    return {
      stickyReady: false,
    };
  },
  computed: {
    stickyEnabled() {
      return this.layout.stickyFilters;
    },
    filterSetScope() {
      return `${this.layout.modelType.typeSlug}:${this.layout.id}`;
    },
  },
  watch: {
    "layout.collection.filterQueryValues": {
      handler() {
        this.persistStickyState();
      },
    },
    "layout.collection.optionQueryValues": {
      handler() {
        this.persistStickyState();
      },
      deep: true,
    },
    "layout.collection.columns": {
      handler() {
        this.persistStickyState();
      },
    },
    "layout.collection.fullExport": {
      handler() {
        this.persistStickyState();
      },
    },
  },
  methods: {
    persistStickyState() {
      if (!this.stickyEnabled || !this.stickyReady) return;

      writeStickyFilterState(
        this.filterSetScope,
        this.layout.collection.hasMeaningfulQueryState
          ? readCollectionQueryState(this.layout.collection)
          : {}
      );
    },
    // Non-router default: apply state straight to the collection, the same
    // fallback CollectionLayout.vue's loadFilterSetState uses for the
    // saved-filter-set menu on a page with no @load-filter-set listener.
    // List.vue overrides this with a router-aware version so a sticky
    // restore stays shareable and the back button keeps working.
    loadQueryState(state) {
      this.layout.collection.clearFiltersExcept([]);
      this.layout.collection.dynamicFilterController.clear();
      this.layout.collection.resetQueryOptionsExcept([]);
      this.layout.collection.applyStagedFilters();

      applyCollectionQueryState(this.layout.collection, state);

      return Promise.resolve();
    },
    initStickyFilters() {
      const hasIncomingCollectionState = Object.keys(
        this.$route.query
      ).some(
        (key) =>
          key.indexOf("_") === 0 || this.layout.collection.getFilter(key)
      );

      if (!this.stickyEnabled) return;

      const stickyState = hasIncomingCollectionState
        ? null
        : readStickyFilterState(this.filterSetScope);

      if (stickyState) {
        this.loadQueryState(stickyState).finally(() => {
          setTimeout(() => {
            this.stickyReady = true;
          }, 150);
        });
      } else {
        this.stickyReady = true;
      }
    },
  },
};
